import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faBullhorn,
  faEye,
  faEyeSlash,
  faUsers,
  faTimes,
  faImage,
  faCheck,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { announcementService } from '../../../services/announcementService';

const ManageAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    targetAudience: 'all',
    imageData: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService.getAll();
      setAnnouncements(data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      setError('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const payload = { ...formData };
      await announcementService.create(payload);
      await fetchAnnouncements();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      setError('Failed to create announcement: ' + error.message);
    }
  };

  const handleUpdateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const payload = { ...formData };
      await announcementService.update(editingAnnouncement.id, payload);
      await fetchAnnouncements();
      setEditingAnnouncement(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update announcement:', error);
      setError('Failed to update announcement: ' + error.message);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await announcementService.delete(id);
      await fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      setError('Failed to delete announcement: ' + error.message);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      setError('');
      await announcementService.update(announcement.id, { 
        isActive: !announcement.isActive 
      });
      await fetchAnnouncements();
    } catch (error) {
      console.error('Failed to toggle announcement status:', error);
      setError('Failed to update announcement status: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      message: '',
      targetAudience: 'all',
      imageData: ''
    });
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      message: announcement.message || '',
      targetAudience: announcement.targetAudience || 'all',
      imageData: announcement.imageData || ''
    });
  };

  // Image file to base64 data URL
  const handleImageChange = (file) => {
    if (!file) {
      setFormData(prev => ({ ...prev, imageData: '' }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, imageData: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'user': return 'Users';
      case 'ecommerce': return 'E-commerce';
      case 'agent': return 'Agents';
      default: return 'All Users';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Announcements</h1>
              <p className="mt-1 text-sm text-gray-600">Create and manage system-wide announcements</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center transition-colors transform transition-transform duration-300 hover:scale-105 active:scale-95"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New Announcement
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 animate-fade-in">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
          {announcements.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faBullhorn} className="text-gray-400 text-xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements yet</h3>
              <p className="text-gray-500 mb-6">Create your first announcement to communicate with users.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center mx-auto transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create Announcement
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audience
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {announcements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        {announcement.imageData ? (
                          <img src={announcement.imageData} alt="announcement" className="w-16 h-16 object-cover rounded-xl" />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                            <FontAwesomeIcon icon={faImage} />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 max-w-md">{announcement.message}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <FontAwesomeIcon icon={faUsers} className="mr-1" />
                          {getAudienceLabel(announcement.targetAudience)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          announcement.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          <FontAwesomeIcon icon={announcement.isActive ? faEye : faEyeSlash} className="mr-1" />
                          {announcement.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleToggleActive(announcement)}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                              announcement.isActive ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-green-100 text-green-600 hover:bg-green-200'
                            }`}
                            title={announcement.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <FontAwesomeIcon icon={announcement.isActive ? faEyeSlash : faEye} />
                          </button>
                          <button
                            onClick={() => openEditModal(announcement)}
                            className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center transition-colors"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                            className="w-10 h-10 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center transition-colors"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200 mt-6">
          <div className="flex items-start">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-800">Announcement Information</p>
              <p className="text-xs text-blue-700 mt-1">Announcements are displayed to users based on their target audience. Active announcements will be visible to the selected user groups.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAnnouncement) && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => {
            setShowCreateModal(false);
            setEditingAnnouncement(null);
            resetForm();
          }}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-transform duration-300 scale-95 animate-scale-in">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                  </h3>
                  <button 
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingAnnouncement(null);
                      resetForm();
                    }} 
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              </div>
              
              <form onSubmit={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    placeholder="Enter announcement message"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image (optional)</label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <FontAwesomeIcon icon={faImage} className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e.target.files?.[0])}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {formData.imageData && (
                    <div className="mt-4 border rounded-xl bg-gray-50 overflow-hidden">
                      <img src={formData.imageData} alt="preview" className="w-full h-48 object-cover" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors appearance-none"
                  >
                    <option value="all">All Users</option>
                    <option value="user">Users Only</option>
                    <option value="ecommerce">E-commerce Only</option>
                    <option value="agent">Agents Only</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingAnnouncement(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors transform transition-transform duration-300 hover:scale-105 active:scale-95"
                  >
                    {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageAnnouncements;